/**
 * Service for managing Stripe Customer Portal sessions
 */

import { buildApiUrl } from '../utils/apiConfig';

export interface CustomerPortalSessionResponse {
  portalUrl: string;
}

/**
 * Creates a Stripe Customer Portal session URL
 * Allows authenticated users to:
 * - Upgrade or downgrade subscription plans (with automatic proration)
 * - Update payment methods securely (PCI compliant)
 * - Cancel subscriptions with built-in confirmation dialogs
 * - View billing history and invoices
 *
 * @param returnUrl - The URL where users will be redirected after completing their session
 * @returns Promise<CustomerPortalSessionResponse> - Contains the Stripe-hosted Customer Portal URL
 * @throws Error if authentication fails, user has no subscription, or request fails
 */
export const createCustomerPortalSession = async (returnUrl: string): Promise<CustomerPortalSessionResponse> => {
  const url = buildApiUrl('api/subscriptions/create-portal-session');

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include', // Send HTTP-only cookies
    body: JSON.stringify({ returnUrl })
  });

  if (response.status === 401) {
    throw new Error('Session expired. Please log in again.');
  }

  if (response.status === 400) {
    throw new Error('Return URL is required');
  }

  if (response.status === 500) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || 'User does not have a Stripe customer ID');
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to create portal session: ${errorText || response.statusText}`);
  }

  const data = await response.json();
  return { portalUrl: data.url || data.portalUrl };
};

